import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Sparkles, TrendingUp, Mail, ArrowRight, Zap } from "lucide-react";

export default function Dashboard() {
  const { user, gmailConnected } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const sent = data?.usage?.sent || 0;
  const quota = data?.plan?.quota || 10;
  const remaining = data?.remaining ?? Math.max(quota - sent, 0);
  const pct = Math.min((sent / quota) * 100, 100);

  return (
    <AppShell>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="label-xs">Dashboard</span>
          <h1 className="mt-2 text-3xl md:text-4xl font-display tracking-tighter">
            Hi {user?.name?.split(" ")[0] || "there"}.
          </h1>
        </div>
        <Link to="/generate" className="btn-primary" data-testid="dashboard-generate-cta">
          Generate new email <Sparkles className="h-4 w-4" />
        </Link>
      </div>

      {/* Stats grid */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="tech-card p-6" data-testid="stat-sent">
          <div className="flex items-center justify-between">
            <span className="label-xs">Used this month</span>
            <Mail className="h-4 w-4 text-slate-400" />
          </div>
          <div className="mt-4 text-4xl font-display tracking-tighter">{sent}</div>
          <div className="mt-4 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-[#0047FF] transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-2 text-xs text-slate-500">{pct.toFixed(0)}% of {quota} limit</p>
        </div>

        <div className="tech-card p-6" data-testid="stat-remaining">
          <div className="flex items-center justify-between">
            <span className="label-xs">Remaining</span>
            <TrendingUp className="h-4 w-4 text-slate-400" />
          </div>
          <div className="mt-4 text-4xl font-display tracking-tighter">{remaining}</div>
          <p className="mt-4 text-xs text-slate-500">Resets at the start of next month</p>
        </div>

        <div className="tech-card p-6" data-testid="stat-plan">
          <div className="flex items-center justify-between">
            <span className="label-xs">Current plan</span>
            <Zap className="h-4 w-4 text-slate-400" />
          </div>
          <div className="mt-4 text-4xl font-display tracking-tighter">{data?.plan?.name || "Free"}</div>
          <p className="mt-4 text-xs text-slate-500">
            {data?.plan?.price ? `$${data.plan.price}/month` : "No card required"}
          </p>
          {data?.plan?.id === "free" && (
            <Link to="/billing" className="mt-4 inline-flex items-center gap-1 text-sm text-[#0047FF] font-medium" data-testid="upgrade-plan-cta">
              Upgrade <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      </div>

      {/* Gmail connection banner */}
      {!gmailConnected && (
        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="gmail-connect-banner">
          <div>
            <div className="font-display font-medium text-amber-900">Connect Gmail to start sending</div>
            <div className="text-sm text-amber-800 mt-1">We need permission to send — never to read — your Gmail.</div>
          </div>
          <Link to="/profile" className="bg-amber-900 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-amber-950">Connect Gmail</Link>
        </div>
      )}

      {/* Recent */}
      <div className="mt-10">
        <div className="flex items-end justify-between">
          <span className="label-xs">Recent emails</span>
          <Link to="/history" className="text-sm text-slate-600 hover:text-slate-900">View all →</Link>
        </div>
        <div className="mt-4 tech-card overflow-hidden">
          {(data?.recent || []).length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              No emails sent yet. <Link to="/generate" className="text-[#0047FF]">Send your first one →</Link>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="recent-emails-table">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-6 py-3 font-medium label-xs">To</th>
                  <th className="text-left px-6 py-3 font-medium label-xs">Subject</th>
                  <th className="text-left px-6 py-3 font-medium label-xs">When</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="px-6 py-4 font-mono text-xs text-slate-700">{r.to_email}</td>
                    <td className="px-6 py-4 text-slate-900 truncate max-w-md">{r.subject}</td>
                    <td className="px-6 py-4 text-slate-500 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AppShell>
  );
}
